import { useState, useEffect } from 'react';
import { User, Plus, Edit2, Trash2, Search, X, Eye, EyeOff } from 'lucide-react';
import userService, { User as UserType, CreateUserData, UpdateUserData } from '../../services/userService';

type ModalType = 'create' | 'edit' | 'delete' | null;

const UsersView = () => {
    const [users, setUsers] = useState<UserType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [modalType, setModalType] = useState<ModalType>(null);
    const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Form states
    const [formData, setFormData] = useState<CreateUserData>({
        username: '',
        email: '',
        password: '',
        role: 'USER',
        companyName: '',
        rut: '',
        address: '',
        phone: '',
        contactName: '',
        contactPosition: ''
    });
    const [confirmEmail, setConfirmEmail] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [formErrors, setFormErrors] = useState<Partial<CreateUserData> & { confirmEmail?: string; confirmPassword?: string }>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await userService.getAllUsers();
            setUsers(response.users);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreateModal = () => {
        setModalType('create');
        setFormData({
            username: '',
            email: '',
            password: '',
            role: 'USER',
            companyName: '',
            rut: '',
            address: '',
            phone: '',
            contactName: '',
            contactPosition: ''
        });
        setConfirmEmail('');
        setConfirmPassword('');
        setFormErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
    };

    const handleOpenEditModal = (user: UserType) => {
        setSelectedUser(user);
        setModalType('edit');
        setFormData({
            username: user.username,
            email: user.email,
            password: '',
            role: user.role,
            companyName: user.companyName || '',
            rut: user.rut || '',
            address: user.address || '',
            phone: user.phone || '',
            contactName: user.contactName || '',
            contactPosition: user.contactPosition || ''
        });
        setConfirmEmail('');
        setConfirmPassword('');
        setFormErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
    };

    const handleOpenDeleteModal = (user: UserType) => {
        setSelectedUser(user);
        setModalType('delete');
    };

    const handleCloseModal = () => {
        setModalType(null);
        setSelectedUser(null);
        setConfirmEmail('');
        setConfirmPassword('');
        setFormErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
    };

    const validateForm = (): boolean => {
        const errors: Partial<CreateUserData> & { confirmEmail?: string; confirmPassword?: string } = {};

        if (!formData.username.trim()) {
            errors.username = 'El nombre de usuario es requerido';
        }

        if (!formData.email.trim()) {
            errors.email = 'El email es requerido';
        } else if (!userService.isValidEmail(formData.email)) {
            errors.email = 'Email inválido';
        } else if (!confirmEmail.trim()) {
            errors.confirmEmail = 'La confirmación de email es requerida';
        } else if (formData.email !== confirmEmail) {
            errors.confirmEmail = 'Los emails no coinciden';
        }

        if (modalType === 'create') {
            if (!formData.password) {
                errors.password = 'La contraseña es requerida';
            } else if (!userService.isValidPassword(formData.password)) {
                errors.password = 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número';
            } else if (!confirmPassword) {
                errors.confirmPassword = 'La confirmación de contraseña es requerida';
            } else if (formData.password !== confirmPassword) {
                errors.confirmPassword = 'Las contraseñas no coinciden';
            }
        } else if (modalType === 'edit' && formData.password) {
            if (!userService.isValidPassword(formData.password)) {
                errors.password = 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número';
            } else if (!confirmPassword) {
                errors.confirmPassword = 'La confirmación de contraseña es requerida';
            } else if (formData.password !== confirmPassword) {
                errors.confirmPassword = 'Las contraseñas no coinciden';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateUser = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError('');
            await userService.createUser(formData);
            setSuccessMessage('Usuario creado exitosamente');
            handleCloseModal();
            loadUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al crear usuario');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateUser = async () => {
        if (!selectedUser || !validateForm()) return;

        try {
            setSubmitting(true);
            setError('');

            const updateData: UpdateUserData = {
                username: formData.username,
                email: formData.email,
                role: formData.role,
                companyName: formData.companyName,
                rut: formData.rut,
                address: formData.address,
                phone: formData.phone,
                contactName: formData.contactName,
                contactPosition: formData.contactPosition
            };

            if (formData.password) {
                updateData.password = formData.password;
            }

            await userService.updateUser(selectedUser.id, updateData);
            setSuccessMessage('Usuario actualizado exitosamente');
            handleCloseModal();
            loadUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al actualizar usuario');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            setSubmitting(true);
            setError('');
            await userService.deleteUser(selectedUser.id);
            setSuccessMessage('Usuario eliminado exitosamente');
            handleCloseModal();
            loadUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al eliminar usuario');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (user: UserType) => {
        try {
            setError('');
            await userService.toggleUserStatus(user.id, !user.active);
            setSuccessMessage(`Usuario ${!user.active ? 'activado' : 'desactivado'} exitosamente`);
            loadUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al cambiar estado del usuario');
        }
    };

    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.companyName && user.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="p-1">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Gestión de Usuarios</h2>
                <p className="text-gray-600">Administra los usuarios del sistema</p>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <span className="text-green-800">{successMessage}</span>
                    <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
                        <X className="h-5 w-5" />
                    </button>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                    <span className="text-red-800">{error}</span>
                    <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
                        <X className="h-5 w-5" />
                    </button>
                </div>
            )}

            {/* Actions Bar */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                        type="text"
                        placeholder="Buscar usuarios..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                    />
                </div>
                <button
                    onClick={handleOpenCreateModal}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-[#144A7B] text-white rounded-lg hover:bg-[#0d3354] transition-colors"
                >
                    <Plus className="h-5 w-5" />
                    Nuevo Usuario
                </button>
            </div>

            {/* Users Table */}
            {loading ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['Usuario', 'Empresa', 'RUT', 'Rol', 'Estado', 'Acciones'].map(col => (
                                        <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200 animate-pulse">
                                {[...Array(6)].map((_, i) => (
                                    <tr key={i}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 bg-gray-200 rounded-full" />
                                                <div>
                                                    <div className="h-4 bg-gray-200 rounded w-28 mb-1" />
                                                    <div className="h-3 bg-gray-200 rounded w-36" />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                                        <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                                        <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-16" /></td>
                                        <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-20" /></td>
                                        <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-20" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <User className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-gray-600">
                        {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Usuario
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Empresa
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rol
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Creado
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-[#144A7B] rounded-full flex items-center justify-center">
                                                    <span className="text-white font-medium">
                                                        {user.username.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {user.username}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{user.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {user.companyName || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${userService.getRoleBadgeColor(user.role)}`}>
                                                {userService.getRoleBadge(user.role)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => handleToggleStatus(user)}
                                                className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${userService.getStatusBadgeColor(user.active)}`}
                                            >
                                                {userService.getStatusBadge(user.active)}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(user.createdAt).toLocaleDateString('es-CL')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenEditModal(user)}
                                                    className="text-[#144A7B] hover:text-[#0d3354] p-1 rounded hover:bg-gray-100"
                                                    title="Editar usuario"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenDeleteModal(user)}
                                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-gray-100"
                                                    title="Eliminar usuario"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {(modalType === 'create' || modalType === 'edit') && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">
                                {modalType === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}
                            </h3>

                            <div className="space-y-4">
                                {/* Username */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre de Usuario *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.username}
                                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="Ingrese nombre de usuario"
                                    />
                                    {formErrors.username && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.username}</p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="usuario@ejemplo.com"
                                    />
                                    {formErrors.email && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                                    )}
                                </div>

                                {/* Confirm Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirmar Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={confirmEmail}
                                        onChange={(e) => setConfirmEmail(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="usuario@ejemplo.com"
                                    />
                                    {formErrors.confirmEmail && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.confirmEmail}</p>
                                    )}
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contraseña {modalType === 'create' ? '*' : '(dejar vacío para no cambiar)'}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                            placeholder="Ingrese contraseña"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                    {formErrors.password && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                                    )}
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirmar Contraseña {modalType === 'create' ? '*' : '(solo si cambias la contraseña)'}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                            placeholder="Repita la contraseña"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                    {formErrors.confirmPassword && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
                                    )}
                                </div>

                                {/* Role */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Rol *
                                    </label>
                                    <select
                                        value={formData.role}
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value as 'ADMIN' | 'USER' })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                    >
                                        <option value="USER">Usuario</option>
                                        <option value="ADMIN">Administrador</option>
                                    </select>
                                </div>

                                {/* Company Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre de la Empresa
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.companyName}
                                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="Ingrese nombre de la empresa"
                                    />
                                </div>

                                {/* RUT */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        RUT
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.rut}
                                        onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="12.345.678-9"
                                    />
                                </div>

                                {/* Address */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Dirección
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="Ingrese dirección"
                                    />
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Teléfono
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="+56 9 1234 5678"
                                    />
                                </div>

                                {/* Contact Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre de Contacto
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.contactName}
                                        onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="Ingrese nombre de contacto"
                                    />
                                </div>

                                {/* Contact Position */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cargo de Contacto
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.contactPosition}
                                        onChange={(e) => setFormData({ ...formData, contactPosition: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A7B] focus:border-transparent"
                                        placeholder="Ingrese cargo de contacto"
                                    />
                                </div>
                            </div>

                            {/* Modal Actions */}
                            <div className="mt-6 flex gap-3 justify-end">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={submitting}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={modalType === 'create' ? handleCreateUser : handleUpdateUser}
                                    disabled={submitting}
                                    className="px-4 py-2 bg-[#144A7B] text-white rounded-lg hover:bg-[#0d3354] disabled:opacity-50 flex items-center gap-2"
                                >
                                    {submitting && (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    )}
                                    {modalType === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {modalType === 'delete' && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Confirmar Eliminación</h3>
                        <p className="text-gray-600 mb-6">
                            ¿Estás seguro de que deseas eliminar al usuario <strong>{selectedUser.username}</strong>?
                            Esta acción no se puede deshacer.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={handleCloseModal}
                                disabled={submitting}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                disabled={submitting}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {submitting && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                )}
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersView;
